import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, DataTable, Field, Modal, StatusBadge, type Column, type StatusTone } from "@fawkes/ui";

import type { Locale } from "../../i18n/messages";
import { formatDateTime } from "../portal/format";
import type { AdminOrganization, IssuedPairing, Pairing } from "./types";

// Epic 10: backoffice pairing — a platform admin issues a one-time code (15 min, bound to the backoffice's
// address) that the backoffice redeems to register itself; the code is shown exactly once. The list shows
// the recent pairings with their progress, and open ones can be revoked.

/** The grantable M2M scopes (server-side catalogue M2mScopes), all ticked by default. */
export const PAIRING_SCOPES = [
  "foster.activate",
  "foster.deactivate",
  "foster.update",
  "absences.read",
  "absences.write",
  "nazorg.invite",
  "publications.write",
  "whatsapp.read",
];

const STATUS_TONE: Record<Pairing["status"], StatusTone> = {
  ISSUED: "neutral",
  REDEEMED: "warning",
  CONFIRMED: "success",
  EXPIRED: "neutral",
  REVOKED: "danger",
};

const CLOSED_STATUSES: Pairing["status"][] = ["EXPIRED", "REVOKED"];

function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

/**
 * Issue a pairing code, for a new organization or (with `organization`) to re-pair an existing one. Mount it
 * only while open — its fields are initialised from `organization` on mount.
 */
export function PairingDialog({
  organization,
  onClose,
  onIssued,
}: {
  organization?: AdminOrganization | null;
  onClose: () => void;
  /** Called after the code is issued. Must not unmount this dialog: the code is shown in it, exactly once. */
  onIssued?: () => void;
}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { mutate, isLoading } = useCustomMutation();
  const [displayName, setDisplayName] = useState(organization?.displayName ?? "");
  const [backofficeUrl, setBackofficeUrl] = useState(organization?.baseUrl ?? "");
  const [scopes, setScopes] = useState<string[]>(PAIRING_SCOPES);
  const [error, setError] = useState<string | undefined>();
  const [issued, setIssued] = useState<IssuedPairing | null>(null);
  const [copied, setCopied] = useState(false);

  function toggleScope(scope: string) {
    setScopes((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));
  }

  function submit() {
    setError(undefined);
    if (backofficeUrl.trim().length === 0) {
      setError(t("admin.pairing.dialog.error.url"));
      return;
    }
    mutate(
      {
        url: "admin/pairings",
        method: "post",
        values: {
          displayName: displayName.trim() || null,
          backofficeUrl: backofficeUrl.trim(),
          scopes: PAIRING_SCOPES.filter((s) => scopes.includes(s)).join(" "),
          organizationId: organization?.id ?? null,
        },
      },
      {
        onSuccess: (res) => {
          setIssued((res?.data as IssuedPairing) ?? null);
          onIssued?.();
        },
        onError: (e) =>
          setError(
            t(statusOf(e) === 400 ? "admin.pairing.dialog.error.invalid" : "admin.pairing.dialog.error.generic"),
          ),
      },
    );
  }

  async function copyCode() {
    if (!issued) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.code);
    } catch {
      setError(t("admin.pairing.code.copyError"));
      return;
    }
    // Drop the plaintext from state once it is on the clipboard.
    setIssued({ ...issued, code: "" });
    setCopied(true);
  }

  return (
    <Modal
      open
      title={t("admin.pairing.dialog.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("admin.common.close")}
          </Button>
          {issued ? null : (
            <Button disabled={isLoading} onClick={submit}>
              {t("admin.pairing.dialog.submit")}
            </Button>
          )}
        </>
      }
    >
      {issued ? (
        <div className="fawkes-admin__secret" role="status">
          <strong>{t("admin.pairing.code.title")}</strong>
          {copied ? (
            <p className="fawkes-portal__muted">{t("admin.pairing.code.copied")}</p>
          ) : (
            <>
              <p className="fawkes-portal__muted">
                {t("admin.pairing.code.once")} {formatDateTime(issued.pairing.expiresAt, locale)}
              </p>
              <code className="fawkes-admin__secret-value">{issued.code}</code>
              <Button variant="secondary" onClick={() => void copyCode()}>
                {t("admin.pairing.code.copy")}
              </Button>
            </>
          )}
          {error ? (
            <p className="fawkes-portal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="fawkes-admin__detail">
          <Field
            label={t("admin.pairing.dialog.displayName")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Field
            label={t("admin.pairing.dialog.backofficeUrl")}
            placeholder="https://"
            value={backofficeUrl}
            onChange={(e) => setBackofficeUrl(e.target.value)}
          />
          <p className="fawkes-portal__muted">{t("admin.pairing.dialog.backofficeUrl.help")}</p>
          <fieldset className="fawkes-admin__fieldset">
            <legend>{t("admin.pairing.dialog.scopes")}</legend>
            {PAIRING_SCOPES.map((scope) => (
              <CheckboxField
                key={scope}
                label={scope}
                checked={scopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
            ))}
          </fieldset>
          {error ? (
            <p className="fawkes-portal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/** The recent pairings, the "pair a backoffice" action, and revoke for open ones. */
export function PairingPanel() {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<Pairing[]>({ url: "admin/pairings", method: "get" });
  const { mutate, isLoading: mutating } = useCustomMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showClosed, setShowClosed] = useState(false);
  const allRows = Array.isArray(data?.data) ? data.data : [];
  // Expired and revoked pairings are history: hidden unless asked for, so the list shows what still matters.
  const closedCount = allRows.filter((p) => CLOSED_STATUSES.includes(p.status)).length;
  const rows = showClosed ? allRows : allRows.filter((p) => !CLOSED_STATUSES.includes(p.status));

  function revoke(pairing: Pairing) {
    setError(undefined);
    mutate(
      { url: `admin/pairings/${pairing.id}`, method: "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setError(t("admin.pairing.revokeError")),
      },
    );
  }

  const columns: Column<Pairing>[] = [
    {
      header: t("admin.pairing.column.status"),
      cell: (p) => <StatusBadge tone={STATUS_TONE[p.status]}>{t(`admin.pairing.status.${p.status}`)}</StatusBadge>,
    },
    { header: t("admin.pairing.column.name"), cell: (p) => p.displayName || "—" },
    { header: t("admin.pairing.column.origin"), cell: (p) => <code>{p.expectedOrigin}</code> },
    {
      header: t("admin.pairing.column.until"),
      cell: (p) =>
        p.status === "ISSUED"
          ? formatDateTime(p.expiresAt, locale)
          : p.status === "REDEEMED"
            ? formatDateTime(p.confirmDeadline, locale)
            : "—",
    },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (p) =>
        p.status === "ISSUED" || p.status === "REDEEMED" ? (
          <Button variant="secondary" disabled={mutating} onClick={() => revoke(p)}>
            {t("admin.pairing.revoke")}
          </Button>
        ) : null,
    },
  ];

  return (
    <section className="fawkes-admin__pairings">
      <h3 className="fawkes-admin__section">{t("admin.pairing.title")}</h3>
      <p className="fawkes-portal__muted">{t("admin.pairing.help")}</p>
      <div className="fawkes-admin__toolbar">
        <Button onClick={() => setDialogOpen(true)}>{t("admin.pairing.open")}</Button>
        {closedCount > 0 ? (
          <CheckboxField
            label={`${t("admin.pairing.showClosed")} (${closedCount})`}
            checked={showClosed}
            onChange={() => setShowClosed((current) => !current)}
          />
        ) : null}
      </div>
      {error ? (
        <p className="fawkes-portal__error" role="alert">
          {error}
        </p>
      ) : null}
      {isLoading ? (
        <p className="fawkes-portal__muted">{t("state.loading")}</p>
      ) : isError ? (
        <p className="fawkes-portal__error" role="alert">
          {t("admin.pairing.error")}
        </p>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(p) => p.id}
          empty={
            <span className="fawkes-portal__muted">
              {t(allRows.length > 0 ? "admin.pairing.emptyOpen" : "admin.pairing.empty")}
            </span>
          }
        />
      )}
      {dialogOpen ? <PairingDialog onClose={() => setDialogOpen(false)} onIssued={() => void refetch()} /> : null}
    </section>
  );
}
